import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/app/providers/ToastProvider";
import {
  listSites,
  syncCookieCloud,
  testSite,
  type SiteView,
} from "@/features/sites/api";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/EmptyState";
import { PageHeader } from "@/shared/ui/PageHeader";
import { Spinner } from "@/shared/ui/Spinner";

/** 站点列表：不展示 Cookie/密钥；支持连通测试与 CookieCloud */
export function SitesPage() {
  const { toast } = useToast();
  const [items, setItems] = useState<SiteView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | "cloud" | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listSites());
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "加载失败（需要超级用户权限）",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="站点"
        description={`${items.length} 个站点 · Cookie 永不在界面展示`}
        actions={
          <>
            <Button
              variant="ghost"
              disabled={busyId === "cloud"}
              onClick={() => {
                setBusyId("cloud");
                void syncCookieCloud()
                  .then(() => toast("CookieCloud 同步已启动", "success"))
                  .catch((e) =>
                    toast(e instanceof Error ? e.message : "同步失败", "error"),
                  )
                  .finally(() => setBusyId(null));
              }}
            >
              CookieCloud
            </Button>
            <Button variant="ghost" onClick={() => void load()} disabled={loading}>
              刷新
            </Button>
          </>
        }
      />

      {loading ? (
        <div className="flex items-center gap-2 text-[13px] text-fg-muted">
          <Spinner /> 加载中…
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-[var(--radius-md)] border border-danger/30 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {error}
        </div>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <EmptyState
          title="暂无站点"
          description="可通过 CookieCloud 同步，或使用后端/官方配置导入"
        />
      ) : null}

      <ul className="flex flex-col gap-2">
        {items.map((s) => (
          <li
            key={s.id}
            className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-md)] border border-border bg-bg-card px-3 py-2.5"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-[14px] font-medium">{s.name}</h3>
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[11px]",
                    s.is_active
                      ? "bg-success/15 text-success"
                      : "bg-bg-hover text-fg-subtle",
                  )}
                >
                  {s.is_active ? "启用" : "停用"}
                </span>
                {s.has_cookie ? (
                  <span className="text-[11px] text-fg-subtle">Cookie ✓</span>
                ) : (
                  <span className="text-[11px] text-danger">无 Cookie</span>
                )}
                {s.proxy ? (
                  <span className="text-[11px] text-fg-subtle">代理</span>
                ) : null}
              </div>
              <p className="mt-0.5 text-[12px] text-fg-subtle">
                {s.domain}
                {s.pri != null ? ` · 优先级 ${s.pri}` : ""}
              </p>
            </div>
            <Button
              variant="ghost"
              disabled={busyId === s.id}
              onClick={() => {
                setBusyId(s.id);
                void testSite(s.id)
                  .then(() => toast(`${s.name} 连通正常`, "success"))
                  .catch((e) =>
                    toast(
                      e instanceof Error ? e.message : `${s.name} 测试失败`,
                      "error",
                    ),
                  )
                  .finally(() => setBusyId(null));
              }}
            >
              测试
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
